import React, { useState, useRef } from 'react';
import { Button, FormGroup, InputGroup, Callout, Intent, Spinner, FileInput } from '@blueprintjs/core';
import { IconNames } from '@blueprintjs/icons';
import { observer } from 'mobx-react-lite';
import { useAppStore } from '../../stores/UnifiedStoreProvider';
import api from '../../services/api';
import './AvatarUpload.css';

interface AvatarUploadProps {
  currentAvatarUrl?: string;
  userId: string;
  onAvatarUpdated?: (newAvatarUrl: string) => void;
}

const AvatarUpload: React.FC<AvatarUploadProps> = observer(({
  currentAvatarUrl,
  userId,
  onAvatarUpdated
}) => {
  const [avatarUrl, setAvatarUrl] = useState('');
  const [urlInput, setUrlInput] = useState('');
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const appStore = useAppStore();

  // Fetch current avatar from backend
  const fetchCurrentAvatar = async () => {
    if (!userId) return;
    
    try {
      // Fetch the user's profile to get the avatar URL
      const response = await api.get('/users/profile');
      if (response.data?.avatar) {
        setAvatarUrl(response.data.avatar);
      }
    } catch (err) {
      console.error('Failed to fetch avatar:', err);
    }
  };

  // Load avatar on mount and when userId changes
  React.useEffect(() => {
    fetchCurrentAvatar();
  }, [userId]);

  // Also sync with prop if provided (fallback)
  React.useEffect(() => {
    if (currentAvatarUrl && !avatarUrl) {
      setAvatarUrl(currentAvatarUrl);
    }
  }, [currentAvatarUrl]);

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.match(/^image\/(jpeg|jpg|png|gif|webp)$/)) {
      setError('Please upload a valid image file (JPEG, PNG, GIF, or WebP)');
      return;
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      setError('File size must be less than 5MB');
      return;
    }

    setUploading(true);
    setError(null);
    setSuccess(null);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await api.post('/users/upload/avatar', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      const newAvatarUrl = response.data.avatarUrl;
      setSuccess('Avatar uploaded successfully!');
      
      // Refresh avatar from backend
      await fetchCurrentAvatar();
      
      // Notify parent
      if (onAvatarUpdated) {
        onAvatarUpdated(newAvatarUrl);
      }
      
      // Clear file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to upload avatar');
    } finally {
      setUploading(false);
    }
  };

  const handleUrlSubmit = async () => {
    if (!urlInput.trim()) {
      setError('Please enter a valid URL');
      return;
    }

    // Basic URL validation
    try {
      new URL(urlInput);
    } catch {
      setError('Please enter a valid URL');
      return;
    }

    setUploading(true);
    setError(null);
    setSuccess(null);

    try {
      const response = await api.post('/users/upload/avatar', {
        url: urlInput,
      });

      const newAvatarUrl = response.data.avatarUrl;
      setSuccess('Avatar uploaded successfully!');
      setUrlInput('');
      
      // Refresh avatar from backend
      await fetchCurrentAvatar();
      
      // Notify parent
      if (onAvatarUpdated) {
        onAvatarUpdated(newAvatarUrl);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to upload avatar from URL');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="avatar-upload">
      {/* Current Avatar Preview */}
      <div className="avatar-preview-section">
        <div className="avatar-preview-container">
          {avatarUrl ? (
            <img 
              src={avatarUrl} 
              alt="User avatar" 
              className="avatar-preview"
            />
          ) : (
            <div className="avatar-preview avatar-preview-empty">
              <IconNames.USER />
            </div>
          )}
        </div>
        <div className="avatar-preview-info">
          <h4>Profile Photo</h4>
          <p>Upload a photo or provide a URL</p>
        </div>
      </div>

      {/* Error/Success Messages */}
      {error && (
        <Callout intent={Intent.DANGER} style={{ marginBottom: '15px' }}>
          {error}
        </Callout>
      )}
      {success && (
        <Callout intent={Intent.SUCCESS} style={{ marginBottom: '15px' }}>
          {success}
        </Callout>
      )}

      {/* File Upload */}
      <FormGroup label="Upload from Computer" labelInfo="(5MB max)">
        <FileInput
          text={uploading ? 'Uploading...' : 'Choose file...'}
          onInputChange={handleFileUpload}
          disabled={uploading}
          inputProps={{
            ref: fileInputRef,
            accept: 'image/*',
          }}
          fill
        />
      </FormGroup>

      {/* Divider */}
      <div className="avatar-upload-divider">
        <span>OR</span>
      </div>

      {/* URL Input */}
      <FormGroup label="Image URL">
        <div style={{ display: 'flex', gap: '8px' }}>
          <InputGroup
            placeholder="https://example.com/avatar.jpg"
            value={urlInput}
            onChange={(e) => setUrlInput(e.target.value)}
            disabled={uploading}
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                handleUrlSubmit();
              }
            }}
          />
          <Button
            icon={IconNames.UPLOAD}
            intent={Intent.PRIMARY}
            onClick={handleUrlSubmit}
            disabled={!urlInput.trim() || uploading}
            loading={uploading}
          >
            Upload
          </Button>
        </div>
      </FormGroup>

      {uploading && (
        <div className="avatar-upload-loading">
          <Spinner size={20} />
          <span>Uploading and optimizing...</span>
        </div>
      )}
    </div>
  );
});

AvatarUpload.displayName = 'AvatarUpload';

export default AvatarUpload;
